import React, {createContext, useContext, useEffect, useMemo, useReducer} from 'react';
import {Provider} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {CoreEvent, store} from '@taopix/taopix-core';
import {CoreInterface, TaopixCore} from '@taopix/taopix-core/dist/types/types';
import {ActionType} from 'typesafe-actions';
import {AppActions, AppActionTypes} from './AppActionTypes';
import {useErrorBoundary} from 'react-error-boundary';

export type RootAction = ActionType<typeof import('./AppActionTypes')>;

export type TaopixApp = {
  booted: boolean;
  loaded: boolean;
  children?: any;
  calendarData?: any;
  isDev?: boolean;
};

export type TaopixContextState = {
  state: TaopixApp;
  dispatch: React.Dispatch<RootAction>;
  core: CoreInterface;
};

const initialState: TaopixApp = {
  booted: false,
  loaded: false,
  isDev: false,
};

export const TaopixContext = createContext<TaopixContextState>({} as TaopixContextState);

const appReducer = (state: TaopixApp, action: RootAction): TaopixApp => {
  switch (action.type) {
    case AppActionTypes.SET_IS_LOADED: {
      return {
        ...state,
        loaded: action.payload,
      };
    }
    case AppActionTypes.SET_CALENDAR_DATA: {
      return {
        ...state,
        calendarData: action.payload,
      };
    }
    default:
      return state;
  }
};

export const TaopixProvider = (
  props: Pick<TaopixApp, 'children'> & {
    booted: boolean;
    sessionRef: string;
    core: TaopixCore;
    isDev?: boolean;
  }
) => {
  const {children, booted, sessionRef, isDev, core} = props;
  const baseState = {
    ...initialState,
  };

  if (isDev) {
    baseState.isDev = isDev;
  }

  const [state, dispatch] = useReducer(appReducer, baseState);
  const {showBoundary} = useErrorBoundary();
  const {t} = useTranslation();

  useEffect(() => {
    if (!booted) {
      return;
    }

    // reset the loading flag
    dispatch(AppActions.setIsLoaded(false));

    const loader = core.Core.get('loader');
    loader
      .on('onError', error => {
        showBoundary(error);
      })
      .on('onLoad', data => {
        // let the app know everything has been loaded
        dispatch(AppActions.setIsLoaded(true));
      });

    if (!state.loaded) {
      loader.init(sessionRef);
    }
  }, [booted, sessionRef, core]);

  useEffect(() => {
    if (!booted) {
      return;
    }

    const token = window.Taopix.Core.subscribe(
      CoreEvent.TOKEN_FAILURE,
      (msg: string, payload: {sessionValid: boolean}) => {
        if (!payload.sessionValid) {
          showBoundary({
            name: t('*:str_TitleProjectSessionMismatchError'),
            message: t('*:str_ErrorProjectSessionTokenMismatch'),
            showRefresh: false,
          });
        } else {
          showBoundary({
            message: t('*:str_MessageTokenExpiry'),
            name: t('*:str_TitleTokenExpiry'),
          });
        }
      }
    );

    return () => {
      core.Core.unsubscribe(token);
    };
  }, [booted]);

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
      core: core?.Core || undefined,
    }),
    [state, core, dispatch]
  );

  return (
    <TaopixContext.Provider value={contextValue}>
      <Provider store={store}>{children}</Provider>
    </TaopixContext.Provider>
  );
};

export const useTaopix = () => {
  const context = useContext(TaopixContext);
  if (context === undefined) {
    throw new Error('useTaopix must be used within a TaopixProvider');
  }
  return context;
};
